// The static product catalog: which devices exist, which skins and rooms can
// be worn, which of them the membership unlocks, and what the membership
// costs. The server joins this with a user's entitlements to produce a
// StudioSnapshot; the presenter maps skin ids to materials
// (src/stage/skins.ts) and room ids to room packages (rooms/).

import { LEGAL } from "./legal.ts";
import type { DeviceId, NativeTarget } from "./types.ts";

export interface SkinEntry {
  id: string;
  name: string;
  swatch: string;
  /** `member` colours are not sold one by one: the membership unlocks all of them. */
  tier: "base" | "member";
}

/** The skin id a device wears when it shows the colour its owner mixed. No catalog skin may use it. */
export const CUSTOM_SKIN = "custom";

/** A colour as skins carry it: `#rrggbb`, lower case. Null for anything else. */
export function parseColor(value: unknown): string | null {
  return typeof value === "string" && /^#[0-9a-f]{6}$/i.test(value) ? value.toLowerCase() : null;
}

export interface DeviceEntry {
  id: DeviceId;
  name: string;
  /**
   * The word of the name that is someone's trademark, when the name is more
   * than that word: `Android` in `Android phone`. A notice names the mark,
   * not the phone. Absent, the whole name is the mark.
   */
  mark?: string;
  /** What a short sentence calls the device, when that is less than `name`: `3DS` for the Nintendo 3DS. Absent, `name`. */
  short?: string;
  /** PocketJS target the device's apps are compiled for. */
  target: "psp" | "vita" | "3ds-dev" | "ipodtouch4-dev" | "redmi-1s-dev";
  viewport: [number, number];
  auxiliary?: [number, number];
  /** Raster density of the emulated panel. */
  density: number;
  input: { buttons: boolean; touch: boolean };
  /**
   * How the device can rest in a room: leaning in a `cradle`, or lying on a
   * flat `surface`. A room's slot for the device must offer one of these.
   */
  rests: ReadonlyArray<"cradle" | "surface">;
  /** The first skin is the default. */
  skins: SkinEntry[];
}

export const DEVICES: readonly DeviceEntry[] = [
  {
    id: "psp",
    name: "PSP",
    target: "psp",
    viewport: [480, 272],
    density: 1,
    input: { buttons: true, touch: false },
    rests: ["cradle"],
    skins: [
      { id: "piano-black", name: "Piano Black", swatch: "#17171b", tier: "base" },
      { id: "ceramic-white", name: "Ceramic White", swatch: "#eeece6", tier: "base" },
      { id: "felicia-blue", name: "Felicia Blue", swatch: "#6f9fe8", tier: "member" },
      { id: "rose-pink", name: "Rose Pink", swatch: "#ff7fb0", tier: "member" },
      { id: "mint", name: "Mint", swatch: "#8be0c4", tier: "member" },
      { id: "champagne", name: "Champagne", swatch: "#d9c08a", tier: "member" },
    ],
  },
  {
    id: "vita",
    name: "PS Vita",
    target: "vita",
    viewport: [480, 272],
    density: 2,
    input: { buttons: true, touch: true },
    rests: ["cradle"],
    skins: [
      { id: "black", name: "Black", swatch: "#1b1d21", tier: "base" },
      { id: "glacier-white", name: "Glacier White", swatch: "#eef0f2", tier: "base" },
      { id: "aqua-blue", name: "Aqua Blue", swatch: "#3bbfe0", tier: "member" },
      { id: "lime", name: "Lime", swatch: "#b8e04a", tier: "member" },
      { id: "neon-orange", name: "Neon Orange", swatch: "#ff8a3c", tier: "member" },
      { id: "sakura", name: "Sakura", swatch: "#ff9cc2", tier: "member" },
    ],
  },
  {
    id: "3ds",
    name: "Nintendo 3DS",
    short: "3DS",
    target: "3ds-dev",
    viewport: [400, 240],
    auxiliary: [320, 240],
    density: 1,
    input: { buttons: true, touch: true },
    rests: ["surface", "cradle"],
    skins: [
      { id: "pearl-white", name: "Pearl White", swatch: "#e9e8e2", tier: "base" },
      { id: "black", name: "Black", swatch: "#202124", tier: "base" },
      { id: "aqua", name: "Aqua", swatch: "#35c2d6", tier: "member" },
      { id: "flame-red", name: "Flame Red", swatch: "#e5383b", tier: "member" },
      { id: "grape", name: "Grape", swatch: "#8f6fe0", tier: "member" },
      { id: "sunny", name: "Sunny", swatch: "#ffd23f", tier: "member" },
    ],
  },
  {
    id: "ipod-touch",
    name: "iPod touch",
    target: "ipodtouch4-dev",
    viewport: [320, 480],
    density: 2,
    input: { buttons: false, touch: true },
    rests: ["cradle"],
    skins: [
      { id: "black", name: "Black", swatch: "#15161a", tier: "base" },
      { id: "white", name: "White", swatch: "#f2f2f0", tier: "base" },
      { id: "pink", name: "Pink", swatch: "#ff6f9f", tier: "member" },
      { id: "sky", name: "Sky", swatch: "#4fc3f0", tier: "member" },
      { id: "lemon", name: "Lemon", swatch: "#ffd84a", tier: "member" },
      { id: "violet", name: "Violet", swatch: "#a98bff", tier: "member" },
    ],
  },
  {
    id: "android",
    name: "Android phone",
    mark: "Android",
    target: "redmi-1s-dev",
    viewport: [360, 640],
    density: 2,
    input: { buttons: false, touch: true },
    rests: ["cradle"],
    skins: [
      { id: "graphite", name: "Graphite", swatch: "#1d1f24", tier: "base" },
      { id: "chalk", name: "Chalk", swatch: "#eeeeea", tier: "base" },
      { id: "coral", name: "Coral", swatch: "#ff6f61", tier: "member" },
      { id: "leaf", name: "Leaf", swatch: "#6fcf5a", tier: "member" },
      { id: "cyan", name: "Cyan", swatch: "#2fc4dc", tier: "member" },
      { id: "sunflower", name: "Sunflower", swatch: "#ffc93c", tier: "member" },
    ],
  },
];

export interface RoomEntry {
  id: string;
  title: string;
  /** `premium` rooms are not sold one by one either: the membership unlocks them. */
  tier: "base" | "premium";
  /** False while a room is listed but its package is not shipped yet. */
  available: boolean;
}

export const ROOMS: readonly RoomEntry[] = [
  { id: "workshop", title: "The Workshop", tier: "base", available: true },
  { id: "rooftop", title: "Rooftop at Dusk", tier: "premium", available: false },
  { id: "arcade", title: "Back-room Arcade", tier: "premium", available: false },
];

export const DEFAULT_ROOM = "workshop";

/**
 * The membership plan in US cents: `price` each month, or `annualPrice` for a
 * year at once ($4.99 a month). src/account/contract.ts states the same two
 * amounts as the plans checkout sells; a test holds them equal.
 */
export const MEMBERSHIP = { price: 799, annualPrice: 5988, period: "month" } as const;

/**
 * What a buyer in Japan pays, in yen (a currency with no minor unit), tax
 * included: the JPY currency option of the same two Stripe prices
 * (scripts/setup-stripe.ts writes it). Stripe charges it to a buyer it
 * places in Japan; every other buyer is charged the dollar amount, which the
 * checkout shows in the buyer's own currency.
 */
export const MEMBERSHIP_JPY = { price: 1280, annualPrice: 9800 } as const;

/** The currency a membership is priced in for a buyer. */
export type PriceCurrency = "usd" | "jpy";

/**
 * The membership's prices for a buyer from `country`, the country Cloudflare
 * reports for the request (CF-IPCountry): yen in Japan, as Stripe's checkout
 * charges there, and dollars anywhere else.
 */
export function membershipPrices(country?: string | null): { currency: PriceCurrency; price: number; annualPrice: number } {
  return country?.toUpperCase() === "JP"
    ? { currency: "jpy", ...MEMBERSHIP_JPY }
    : { currency: "usd", price: MEMBERSHIP.price, annualPrice: MEMBERSHIP.annualPrice };
}

// ------------------------------------------------------- public addresses

/**
 * Labels no game may take as its address: names the Studio or its operator
 * may need as hosts of their own, and names that read as official.
 */
export const RESERVED_SLUGS: ReadonlySet<string> = new Set([
  "www", "api", "app", "apps", "account", "accounts", "admin", "assets", "auth", "billing", "blog", "cdn",
  "dev", "docs", "download", "downloads", "ftp", "help", "home", "install", "login", "mail", "news", "play",
  "pocket", "nexus", "root", "runtime", "shop", "static", "status", "store", "studio", "support", "test", "tv",
]);

export const SLUG_MIN = 3;
export const SLUG_MAX = 32;

/**
 * A slug as the Studio stores it, or the reason it cannot be one. A slug is
 * one DNS label: 3 to 32 lowercase letters, digits and single inner hyphens.
 */
export function parseSlug(value: unknown): { slug: string } | { error: string } {
  if (typeof value !== "string") return { error: "An address is text" };
  const slug = value.trim().toLowerCase();
  if (slug.length < SLUG_MIN || slug.length > SLUG_MAX) return { error: `An address is ${SLUG_MIN} to ${SLUG_MAX} characters` };
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)) return { error: "An address uses lowercase letters, digits and single hyphens between them" };
  if (RESERVED_SLUGS.has(slug)) return { error: "That address is reserved" };
  return { slug };
}

/** The slug a title suggests: its letters and digits, hyphenated, cut to fit. Always a valid slug. */
export function slugFor(title: string): string {
  const slug = title
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, SLUG_MAX)
    .replace(/-+$/, "");
  if (slug.length < SLUG_MIN) return slug ? `game-${slug}` : "game";
  return RESERVED_SLUGS.has(slug) ? `${slug.slice(0, SLUG_MAX - 5)}-game` : slug;
}

/**
 * The address of the game host for `slug` beside the Studio at `origin`:
 * `https://tokyo.studio.pocket.nexus` beside `https://studio.pocket.nexus`.
 * A loopback Studio (`http://127.0.0.1:4180`) has its game hosts under
 * `localhost` (`http://tokyo.localhost:4180`), which browsers resolve to
 * this machine.
 */
export function gameUrl(origin: string, slug: string): string {
  const url = new URL(origin);
  const loopback = url.hostname === "127.0.0.1" || url.hostname === "localhost" || url.hostname === "[::1]";
  return `${url.protocol}//${slug}.${loopback ? "localhost" : url.hostname}${url.port ? `:${url.port}` : ""}`;
}

// ----------------------------------------------------------------- handles

export const HANDLE_MIN = 3;
export const HANDLE_MAX = 24;

/**
 * Handles no account may choose: every label reserved as an address, and
 * names that read as the platform's own or as a role on it. An editor is the
 * platform's voice and may take one.
 */
export const RESERVED_HANDLES: ReadonlySet<string> = new Set([
  ...RESERVED_SLUGS,
  "official", "staff", "team", "editor", "editors", "moderator", "mod", "verified", "system", "owner",
  "guest", "anonymous", "everyone", "nobody", "pocketjs", "pocket3d", "pocketnexus", "pocketstudio",
]);

/**
 * Whether a handle is the platform's to give: a reserved name, or any name
 * that starts with `pocket-`. That prefix covers the platform's products
 * (`pocket-nexus`, `pocket-studio`, `pocket-tokyo`) and the handle every
 * account starts with, `pocket-` and eight characters of its id, so no
 * account can choose a name that is another account's first one.
 */
export function reservedHandle(handle: string): boolean {
  return RESERVED_HANDLES.has(handle) || handle.startsWith("pocket-");
}

/**
 * A handle as the Studio stores it, or the reason it cannot be one: 3 to 24
 * lowercase letters, digits and single inner hyphens. What a person types is
 * trimmed, lowered, and may start with the `@` the room shows before a
 * handle. With `reserved`, a name of the platform's is accepted: the caller
 * is an editor.
 */
export function parseHandle(value: unknown, reserved = false): { handle: string } | { error: string } {
  if (typeof value !== "string") return { error: "A handle is text" };
  const handle = value.trim().replace(/^@/, "").toLowerCase();
  if (handle.length < HANDLE_MIN || handle.length > HANDLE_MAX) return { error: `A handle is ${HANDLE_MIN} to ${HANDLE_MAX} characters` };
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(handle)) return { error: "A handle uses lowercase letters, digits and single hyphens between them" };
  if (!reserved && reservedHandle(handle)) return { error: "That handle is reserved" };
  return { handle };
}

// ---------------------------------------------------------------- accounts

/** What one account may hold. */
export const ACCOUNT_LIMITS = {
  /** Projects, drafts and published ones together. */
  projects: 50,
  /** Bytes stored across the builds, packages and site files of every project. */
  bytes: 3 * 1024 * 1024 * 1024,
  /**
   * The same for an editor's account, which is the platform's own shelf: the verified games with
   * every device's package, their past versions and their sites (one game's site with two devices'
   * packs is half a GiB).
   */
  editorBytes: 32 * 1024 * 1024 * 1024,
  /** Addresses held for the account after its games gave them up. One more is free at once. */
  heldAddresses: 50,
  /** Other authors' games the account put on its shelf from their links or Pocket TV. */
  kept: 200,
} as const;

/** How long an address its game gave up stays held for the account that had it, in milliseconds. */
export const SLUG_HOLD_MS = 30 * 24 * 60 * 60 * 1000;

// ------------------------------------------------------------------- sites

/**
 * What the author of a site project nobody verified is told when they deploy
 * a web build. The server refuses with it, and the command line says it
 * before it reads a file.
 */
export const SITE_UNVERIFIED =
  "Pocket Nexus has not verified this project, and a web build is deployed for verified projects only. Its packages and its page in Pocket Studio need no verification.";

/** The longest reason an editor gives for a takedown, in characters: one sentence or two, which the author reads. */
export const TAKEDOWN_REASON_MAX = 300;

/** What an author is told when they publish a game Pocket Nexus took down, and what the card says under the reason. */
export const TAKEN_DOWN = `Pocket Nexus took this game down. It cannot be published until that is lifted. Write to ${LEGAL.contactEmail}.`;

/** What one site deployment may hold. */
export const SITE_LIMITS = {
  /** Files in one deployment. */
  files: 4000,
  /** Bytes in one file. */
  fileBytes: 32 * 1024 * 1024,
  /** Bytes in one deployment. */
  bytes: 1024 * 1024 * 1024,
  /** UTF-8 bytes in one path. */
  pathBytes: 200,
} as const;

/**
 * The directories of the built site that every game host serves the Studio's
 * player and runtime from. The play Worker's static assets answer them before
 * the Worker runs (wrangler.play.jsonc), so a site's own file under one of
 * them would never be reached.
 */
export const PLAYER_DIRECTORIES = ["play", "runtime"] as const;

/**
 * A file's path inside a site as the Studio stores it, or the reason it
 * cannot be one. A path is relative, with `/` between its parts: no leading
 * or trailing slash, no empty part, no `.` or `..` part, no backslash and no
 * control character. `api` as the first part is refused, because a game host
 * answers 404 under `/api/`; so are `play` and `runtime`, because a game host
 * serves the Studio's player there.
 */
export function parseSitePath(value: unknown): { path: string } | { error: string } {
  if (typeof value !== "string" || value === "") return { error: "A file path is text" };
  const says = (reason: string) => ({ error: `${JSON.stringify(value.slice(0, 80))} ${reason}` });
  if (/[\u0000-\u001f\u007f]/.test(value)) return says("has a control character");
  if (value.includes("\\")) return says("has a backslash; paths use /");
  if (value.startsWith("/")) return says("starts with a slash; paths are relative to the site's directory");
  const parts = value.split("/");
  if (parts.some((part) => part === "" || part === "." || part === "..")) return says("has an empty, . or .. part");
  if (parts[0] === "api") return says("is under api/, which a game host does not serve");
  if ((PLAYER_DIRECTORIES as readonly string[]).includes(parts[0]!) && parts.length > 1) {
    return says(`is under ${parts[0]}/, where every game host serves the Studio's player`);
  }
  if (new TextEncoder().encode(value).length > SITE_LIMITS.pathBytes) return says(`is longer than ${SITE_LIMITS.pathBytes} bytes`);
  return { path: value };
}

// ------------------------------------------------------------------ covers

/**
 * What a game's cover picture may be. The room draws it into a window of at
 * most 348 by 310 pixels on a case and smaller on a card and on Pocket TV,
 * so a screenshot at a device's own size is the picture this is for: 480 by
 * 272 on a PSP, 960 by 544 on a PS Vita, 640 by 960 on an iPod touch. The
 * limits leave room for twice that and no more.
 */
export const COVER_LIMITS = {
  /** Bytes in the file. */
  bytes: 2 * 1024 * 1024,
  /** No side is shorter, in pixels. */
  minSide: 64,
  /** No side is longer. */
  maxSide: 2048,
} as const;

// ---------------------------------------------------------------- packages

/** Largest installation package, in bytes. Pocket Tokyo's iPod touch build is 177 MB. */
export const MAX_PACKAGE_BYTES = 512 * 1024 * 1024;

/** A package is uploaded in parts of this size; the last part holds what is left. */
export const PACKAGE_PART_BYTES = 16 * 1024 * 1024;

/**
 * A package's version as the Studio stores it, or the reason it cannot be
 * one: up to 32 letters, digits, dots, underscores, plus signs and hyphens.
 * Absent, the version is the empty string.
 */
export function parseVersion(value: unknown): { version: string } | { error: string } {
  if (value === undefined || value === "") return { version: "" };
  if (typeof value !== "string" || !/^[A-Za-z0-9._+-]{1,32}$/.test(value)) {
    return { error: "A version is up to 32 letters, digits, dots, underscores, plus signs and hyphens" };
  }
  return { version: value };
}

/** The container a package file is, read from its first bytes. */
export type PackageContainer = "pbp" | "3dsx" | "zip";

/**
 * What a package for each target may be: the platform's own file, or a `.zip`
 * of the files to copy when the game is more than one (a PSP game folder, a
 * 3DS program with data for the SD card). Names ending in `.zip`, `.vpk`,
 * `.ipa` and `.apk` are zip archives.
 */
const PACKAGE_FILES: Record<NativeTarget, ReadonlyArray<{ match: RegExp; container: PackageContainer; says: string }>> = {
  psp: [
    { match: /^EBOOT\.PBP$/i, container: "pbp", says: "EBOOT.PBP" },
    { match: /\.zip$/i, container: "zip", says: "a .zip of the PSP/GAME folder" },
  ],
  vita: [
    { match: /\.vpk$/i, container: "zip", says: "a .vpk" },
    { match: /\.zip$/i, container: "zip", says: "a .zip" },
  ],
  "3ds": [
    { match: /\.3dsx$/i, container: "3dsx", says: "a .3dsx" },
    { match: /\.zip$/i, container: "zip", says: "a .zip of the .3dsx and its SD card files" },
  ],
  "ipod-touch": [
    { match: /\.ipa$/i, container: "zip", says: "an .ipa" },
    { match: /\.zip$/i, container: "zip", says: "a .zip" },
  ],
  android: [
    { match: /\.apk$/i, container: "zip", says: "an .apk" },
    { match: /\.zip$/i, container: "zip", says: "a .zip" },
  ],
};

/** The first bytes of each container: "\0PBP", "3DSX", and the local file header of a zip. */
export const PACKAGE_MAGIC: Record<PackageContainer, readonly number[]> = {
  pbp: [0x00, 0x50, 0x42, 0x50],
  "3dsx": [0x33, 0x44, 0x53, 0x58],
  zip: [0x50, 0x4b, 0x03, 0x04],
};

/**
 * The container a file named `filename` must be for `target`, or the reason
 * the name is not accepted. A name is 1 to 80 letters, digits, dots,
 * underscores and hyphens: it goes into a download header as it is.
 */
export function packageContainer(target: NativeTarget, filename: unknown): { container: PackageContainer } | { error: string } {
  if (typeof filename !== "string" || !/^[A-Za-z0-9._-]{1,80}$/.test(filename) || filename.startsWith(".")) {
    return { error: "A package file name is 1 to 80 letters, digits, dots, underscores and hyphens" };
  }
  const accepted = PACKAGE_FILES[target];
  const format = accepted.find((entry) => entry.match.test(filename));
  if (!format) return { error: `A ${target} package is ${accepted.map((entry) => entry.says).join(" or ")}` };
  return { container: format.container };
}

/** What a package's target is called: the device's name. */
export function targetName(target: NativeTarget): string {
  return deviceEntry(target).name;
}

/** A target as a sentence names it: `the PSP`, `the Nintendo 3DS`, `the Android phone`. */
export function targetPhrase(target: NativeTarget): string {
  return `the ${deviceEntry(target).name}`;
}

export function deviceEntry(id: DeviceId): DeviceEntry {
  const entry = DEVICES.find((device) => device.id === id);
  if (!entry) throw new Error(`unknown device ${id}`);
  return entry;
}

/** One app as the build writes it to apps.json. Server and client both read it. */
/**
 * A repository a remix may clone: an https address on GitHub, GitLab or
 * Codeberg naming an owner and a repository, with no credentials, query or
 * fragment. Answers the address without a trailing ".git" or slash, or an
 * error to show.
 */
export function parseRepository(value: unknown): { repository: string } | { error: string } {
  const refused = { error: "A repository is an https address on github.com, gitlab.com or codeberg.org, such as https://github.com/owner/game" };
  if (typeof value !== "string" || value.length > 200) return refused;
  let url: URL;
  try {
    url = new URL(value.trim());
  } catch {
    return refused;
  }
  if (url.protocol !== "https:" || url.username || url.password || url.search || url.hash) return refused;
  if (!["github.com", "gitlab.com", "codeberg.org"].includes(url.hostname)) return refused;
  const parts = url.pathname.replace(/\/+$/, "").replace(/\.git$/, "").split("/").filter(Boolean);
  if (parts.length < 2 || parts.some((part) => !/^[A-Za-z0-9_.-]+$/.test(part) || part === "." || part === "..")) return refused;
  return { repository: `https://${url.hostname}/${parts.join("/")}` };
}

export interface AppCatalogEntry {
  id: string;
  title: string;
  author: string;
  tagline: string;
  /** `starter` apps ship on every shelf. */
  origin: "starter";
  /** True for the projects under starters/: a new game is scaffolded from one of these. */
  template: boolean;
  colors: [string, string];
  /** Screenshot used on the cover, relative to the apps directory. */
  shot: string;
  /** Per-device artifacts, relative to the apps directory. */
  builds: Partial<Record<DeviceId, { js: string; pak: string }>>;
}

export interface AppCatalog {
  apps: AppCatalogEntry[];
}
